


/*

CLASS: BaseModel

DESCRIPTION: 
    This class acts a base model for all the classes (models) that inherit it.
    GET and SET for the models are defined in the Base Model.
    CRUD operations are also defined here
*/

import {
    LoggerUtility
} from '../components/ComponentsLibrary';

export class BaseModel {
    constructor(initialModel) {
        try {
            this.model = new Proxy(initialModel, {
                get: (target, prop) => {
                    // Intercept the 'get' operation and forward it to the model
                    if (prop in target) {
                        return target[prop]; // Return the property value from the model
                    } else {
                        return undefined; // Return undefined if the property doesn't exist
                    }
                },
                set: (target, prop, value) => {
                    target[prop] = value; // Update the model value
                    return true; // Indicate success
                },
            });

            // Example: Both batchStatusModel.lockType and batchStatusModel.model.lockType will work in the calling component
            // Use another Proxy to make the properties of the model directly accessible
            return new Proxy(this, {
                get: (target, prop) => {
                    // Directly access properties of the model through 'this' object
                    if (prop in target) {
                        return target[prop]; // Return the property of BaseModel if it exists
                    } else {
                        // Otherwise, delegate the access to the model (nested Proxy)
                        return target.model[prop]; // Access the property from the model
                    }
                },
                set: (target, prop, value) => {
                    // Intercept 'set' operations and assign values to the model
                    if (prop in target) {
                        target[prop] = value;
                    } else {
                        target.model[prop] = value; // Set the property in the model
                    }
                    return true; // Indicate success
                },
            });
        }
        catch (error) {
            LoggerUtility.ManageErrorLogging(error, "BaseModel", "BaseModel Construtor load");
        }
    }

    getValues() {
        try {
            return this.model;
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "BaseModel - getValues", {});
        }
    }

    // Base class method to assign values to the model
    async assignValues(apiResponse) {
        try {
            let isSuccess = false;
            if (!apiResponse || typeof apiResponse !== "object") return isSuccess;

            // Iterate through object fields and assign values from the API response if they exist
            Object.keys(this.model).forEach((key) => {
                if (apiResponse.hasOwnProperty(key)) {
                    this.model[key] = apiResponse[key];
                    isSuccess = true;
                }
            });

            return { isSuccess: isSuccess, model: this.model };
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "BaseModel - assignValues", {});
        }
    }

    /**
   * Assigns values from an array of objects to the model with validation.
   * @param {Array} array - Array of objects containing API response data.
   * @param {Object} fieldMapping - Mapping of array fields to model fields.
   * @returns {Object} Validation report.
   */
    async assignValuesFromArray(array, fieldMapping) {
        try {
            const validationReport = {
                success: true,
                errors: [],
                modelData: [],
            };

            array.forEach((item, index) => {
                const updatedModel = {};
                let isError = false;

                for (let arrayField in fieldMapping) {
                    const modelField = fieldMapping[arrayField];

                    if (item.hasOwnProperty(arrayField)) {
                        updatedModel[modelField] = item[arrayField];
                    } else {
                        isError = true;
                        validationReport.errors.push({
                            index,
                            field: arrayField,
                            message: `Field "${arrayField}" is missing in array item.`,
                        });
                    }
                }

                if (!isError) {
                    // Update the model with the mapped values if no errors
                    Object.assign(this.model, updatedModel);
                    validationReport.modelData.push({ ...this.model });
                } else {
                    validationReport.success = false; // Mark overall success as false
                }
            });

            return validationReport;
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "BaseModel - assignValuesFromArray", {});
        }
    }

    /**
   * Converts the Proxy model to a plain object.
   * @returns {Object} A plain object representation of the model.
   */
    toObject() {
        try {
            return { ...this.model };
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "BaseModel - toObject", {});
        }
    }

    updateObject(updatedValues) {
        try {
            Object.keys(updatedValues).forEach((key) => {
                if (this.model.hasOwnProperty(key)) {
                    this.model[key] = updatedValues[key];
                }
            });
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "BaseModel - updateObject", {});
        }
    }

    updateKeyValue(key, value) {
        try {
            if (this.model.hasOwnProperty(key)) {
                this.model[key] = value;
            }
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "BaseModel - updateKeyValue", {});
        }
    }

    updateValues(newStateOrKey, value) {
        try {
            if (typeof newStateOrKey === 'object') {
                // Update the entire state object
                this.model = { ...this.model, ...newStateOrKey };
            } else if (typeof newStateOrKey === 'string' && value !== undefined) {
                // Update a specific key-value pair
                if (this.model.hasOwnProperty(newStateOrKey)) {
                    this.model[newStateOrKey] = value;
                }
            }
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "BaseModel - updateValues", {});
        }
    }
}


// Batch Lock Status
class BatchStatusModel extends BaseModel {
    constructor() {
        super({
            lockType: 0,
            lockUserLoginID: 0,
            lockTime: null,
            lockMessage: null
        });
    }
}

// Case Documents - Documents that belong to a Case Number
class CaseDocumentsModel extends BaseModel {
    constructor() {
        super({
            scanDate: null,
            indexDate: null,
            documentGroup: '',
            documentType: '',
            documentSource: '',
            batchDocumentID: 0,
            documentUNCPath: ''
        });
    }
}

// Narrative - Properties required for a state narrative
class NarrativeModel extends BaseModel {
    constructor() {
        super({
            ytracSiteID: 0,
            batchDocumentID: 0,
            docGroupID: 0,
            documentTypeID: 0,
            documentTypeName: '',
            documentSourceTypeName: '',
            noCase: false,
            caseNumber: '',
            loginName: '',
            masterServerDBSchema: '',
            SystemTimeZoneName: ''
        });
    }
}

// NarrativeBuilder - Properties required for a narrative using G2 style note Template ID
class NarrativeBuilderModel extends BaseModel {
    constructor() {
        super({
            ytracSiteID: 0,
            noteTemplateID: 0,
            documentTypeName: '',
            lastIndexTS: '',
            loginName: '',
            masterServerDBSchema: '',
            yTracSite_StateID: 0,
            timeZoneName: 'WhyIsThisNeeded'
        });
    }
}

// Notification Dialog 
class NotificationDialogModel extends BaseModel {
    constructor() {
        super({
            ID: 0,
            Name: '',
            NotificationType: '',
            Title: '',
            Message: ''
        });
    }
}

// Export a singleton instance
export { 
    BatchStatusModel,
    CaseDocumentsModel,
    NarrativeModel,
    NarrativeBuilderModel,
    NotificationDialogModel 
};

// Export instances (optional singleton pattern)
export const batchStatusModel = new BatchStatusModel();
export const caseDocumentsModel = new CaseDocumentsModel();
export const narrativeModel = new NarrativeModel();
export const narrativeBuilderModel = new NarrativeBuilderModel();
export const notificationDialogModel = new NotificationDialogModel();